import { useMemo } from 'react';
import * as Sentry from '@sentry/react';

import type { FactValue } from '@karehero/models';

import { useDispatch } from 'store';
import {
  useCreateManyAnswerMutation,
  useGetCareProfileAnswersQuery,
} from 'store/api/careProfile';
import {
  useConfirmExtractedFactMutation,
  useDismissExtractedFactMutation,
  useGetCareCircleExtractedFactsQuery,
} from 'store/api/inference';
import { newToast } from 'store/slices/uiSlice';
import {
  BENEFITS_CALCULATOR_QUESTION_IDS,
  benefitsCalculatorKnownAnswers,
  type Prediction,
} from 'utils/benefitsCalculatorKnownAnswers';

const isSameAnswer = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

export interface UseBenefitsCalculatorKnownAnswersOptions {
  careCircleID: string;
  /** Where the answers already given are read from, and a changed one saved. */
  careProfileID?: string;
  /** Said in place of the care recipient in each question. */
  careRecipientName: string;
}

/**
 * useBenefitsCalculatorKnownAnswers gives the calculator's questions we already
 * have an answer to, or a prediction of one, and settles them: a changed
 * answer is saved straight to the care profile, dismissing the predictions it
 * replaces, and confirming accepts the predictions left.
 */
export const useBenefitsCalculatorKnownAnswers = ({
  careCircleID,
  careProfileID,
  careRecipientName,
}: UseBenefitsCalculatorKnownAnswersOptions) => {
  const dispatch = useDispatch();
  const [confirmFact] = useConfirmExtractedFactMutation();
  const [dismissFact] = useDismissExtractedFactMutation();
  const [createManyAnswer] = useCreateManyAnswerMutation();

  // A failed load (reported by the base query) leaves nothing known, so the
  // calculator asks every question.
  const { data: facts, isLoading: isLoadingFacts } =
    useGetCareCircleExtractedFactsQuery(
      {
        careCircleId: careCircleID,
        questionIds: BENEFITS_CALCULATOR_QUESTION_IDS,
      },
      { skip: !careCircleID },
    );
  const { data: answers, isLoading: isLoadingAnswers } =
    useGetCareProfileAnswersQuery(careProfileID ?? '', {
      skip: !careProfileID,
    });

  const knownAnswers = useMemo(
    () =>
      benefitsCalculatorKnownAnswers(
        facts?.facts ?? [],
        answers ?? [],
        careRecipientName,
      ),
    [facts, answers, careRecipientName],
  );

  const reportFailure = (err: unknown, description: string) => {
    Sentry.captureException(err);
    dispatch(
      newToast({
        title: 'Something went wrong',
        description,
        variant: 'error',
      }),
    );
  };

  const confirm = (predictions: Prediction[]) =>
    Promise.all(
      predictions.map(({ factId }) =>
        confirmFact({ careCircleId: careCircleID, factId }).unwrap(),
      ),
    );

  /**
   * Saves a question's answer: confirming the predictions that say the same,
   * or saving it to the care profile when none do, and dismissing the rest.
   */
  const saveAnswer = async (questionId: string, value: FactValue) => {
    const known = knownAnswers.find((k) => k.questionId === questionId);
    const selected = Array.isArray(value) ? value : [value];
    if (!known || !selected.length) return;

    const same = known.predictions.filter((p) =>
      isSameAnswer(p.selected, selected),
    );
    const replaced = known.predictions.filter((p) => !same.includes(p));

    try {
      if (same.length) {
        await confirm(same);
      } else {
        if (!careProfileID) throw new Error('No care profile to save to');
        await createManyAnswer({
          careProfileId: careProfileID,
          answers: [
            {
              careProfileId: careProfileID,
              careProfileQuestionId: questionId,
              value:
                known.row.control === 'choices'
                  ? JSON.stringify(selected)
                  : selected[0],
            },
          ],
        }).unwrap();
      }
      await Promise.all(
        replaced.map(({ factId }) =>
          dismissFact({ careCircleId: careCircleID, factId }).unwrap(),
        ),
      );
    } catch (err) {
      reportFailure(err, 'We could not save that change. Please try again.');
    }
  };

  /** Accepts every prediction still shown. Resolves whether they all saved. */
  const confirmPredictions = async (): Promise<boolean> => {
    const predicted = knownAnswers
      .filter((k) => k.row.status === 'inferred')
      .flatMap((k) =>
        k.predictions.filter((p) =>
          isSameAnswer(p.selected, k.row.selected ?? []),
        ),
      );
    try {
      await confirm(predicted);
      return true;
    } catch (err) {
      reportFailure(err, 'We could not save these answers. Please try again.');
      return false;
    }
  };

  return {
    knownAnswers,
    isLoading: isLoadingFacts || isLoadingAnswers,
    saveAnswer,
    confirmPredictions,
  };
};
